<template>
	<div class="mb-4">
		<div class="flex flex-row justify-between">

			<Popper hover arrow :placement="tooltipLocation">
				<template v-if="tooltip" #content>
					<div v-html="tooltip"></div>
				</template>

				<label v-if="props.label" class="mb-2 block leading-6 text-label" :class="props.labelTextSize">
					{{ props.label }}
				</label>
			</Popper>

			<slot name="toggle"></slot>
		</div>
		<div class="group relative inline-block w-full">
			<textarea
				v-if="props.type === 'textarea'"
				:inert="props.disabled || props.useEditModal"
				:placeholder="props.placeholder"
				:autocomplete="props.autocomplete"
				:rows="props.textAreaRows"
				v-model="model"
				:maxLength="props.maxLength"
				@input="inputFilter"
				@beforeinput="beforeInput"
				:class="calculatedClasses()" />

			<!-- Don't set type to props.type, number breaks the watch in firefox (invalid nums get passed as "" breaking maxlength code) -->
			<div v-else :class="{ 'cursor-pointer': !props.disabled && props.useEditModal}" @click="editModalButtonClicked">
				<input
					:inert="props.disabled || props.useEditModal"
					:type="props.type === 'number' ? 'text' : props.type"
					:placeholder="props.placeholder"
					:autocomplete="props.autocomplete"
					v-model="model"
					:maxLength="props.maxLength"
					@input="inputFilter"
					@beforeinput="beforeInput"
					:class="calculatedClasses()"
				/>
			</div>

			<div class="absolute flex h-full right-4 top-0" :class="{'hidden group-hover:flex group-focus-within:flex' : props.hoverEdit && !props.disabled}">
				<div class="my-auto">
					<slot>
						<template v-if="icon === InputIcon.NONE"></template>

						<svg v-else-if="icon === InputIcon.EMAIL" width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="stroke-current group-hover:text-primary-hover" stroke-width="1.5" fill="none">
							<title>Email</title>
							<path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
						</svg>

						<svg v-else-if="icon === InputIcon.PASSWORD_LOCKED" xmlns="http://www.w3.org/2000/svg" class="fill-current group-hover:text-primary-hover" width="20" height="20" viewBox="0 0 24 24">
							<title>Password shown</title>
							<path d="M6 22C5.45 22 4.97933 21.8043 4.588 21.413C4.19667 21.0217 4.00067 20.5507 4 20V10C4 9.45 4.196 8.97933 4.588 8.588C4.98 8.19667 5.45067 8.00067 6 8H7V6C7 4.61667 7.48767 3.43767 8.463 2.463C9.43833 1.48833 10.6173 1.00067 12 1C13.3827 0.999334 14.562 1.487 15.538 2.463C16.514 3.439 17.0013 4.618 17 6V8H18C18.55 8 19.021 8.196 19.413 8.588C19.805 8.98 20.0007 9.45067 20 10V20C20 20.55 19.8043 21.021 19.413 21.413C19.0217 21.805 18.5507 22.0007 18 22H6ZM6 20H18V10H6V20ZM12 17C12.55 17 13.021 16.8043 13.413 16.413C13.805 16.0217 14.0007 15.5507 14 15C13.9993 14.4493 13.8037 13.9787 13.413 13.588C13.0223 13.1973 12.5513 13.0013 12 13C11.4487 12.9987 10.978 13.1947 10.588 13.588C10.198 13.9813 10.002 14.452 10 15C9.998 15.548 10.194 16.019 10.588 16.413C10.982 16.807 11.4527 17.0027 12 17ZM9 8H15V6C15 5.16667 14.7083 4.45833 14.125 3.875C13.5417 3.29167 12.8333 3 12 3C11.1667 3 10.4583 3.29167 9.875 3.875C9.29167 4.45833 9 5.16667 9 6V8Z"/>
						</svg>

						<svg v-else-if="icon === InputIcon.PASSWORD_UNLOCKED" xmlns="http://www.w3.org/2000/svg" class="fill-current group-hover:text-primary-hover" width="20" height="20" viewBox="0 0 24 24">
							<title>Password hidden</title>
							<path d="M6 8H15V6C15 5.16667 14.7083 4.45833 14.125 3.875C13.5417 3.29167 12.8333 3 12 3C11.1667 3 10.4583 3.29167 9.875 3.875C9.29167 4.45833 9 5.16667 9 6H7C7 4.61667 7.48767 3.43767 8.463 2.463C9.43833 1.48833 10.6173 1.00067 12 1C13.3827 0.999334 14.562 1.487 15.538 2.463C16.514 3.439 17.0013 4.618 17 6V8H18C18.55 8 19.021 8.196 19.413 8.588C19.805 8.98 20.0007 9.45067 20 10V20C20 20.55 19.8043 21.021 19.413 21.413C19.0217 21.805 18.5507 22.0007 18 22H6C5.45 22 4.97933 21.8043 4.588 21.413C4.19667 21.0217 4.00067 20.5507 4 20V10C4 9.45 4.196 8.97933 4.588 8.588C4.98 8.19667 5.45067 8.00067 6 8ZM6 20H18V10H6V20ZM12 17C12.55 17 13.021 16.8043 13.413 16.413C13.805 16.0217 14.0007 15.5507 14 15C13.9993 14.4493 13.8037 13.9787 13.413 13.588C13.0223 13.1973 12.5513 13.0013 12 13C11.4487 12.9987 10.978 13.1947 10.588 13.588C10.198 13.9813 10.002 14.452 10 15C9.998 15.548 10.194 16.019 10.588 16.413C10.982 16.807 11.4527 17.0027 12 17Z"/>
						</svg>

						<svg v-else-if="icon === InputIcon.PHONE" class="fill-current group-hover:text-primary-hover" width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
							<title>Phone Number</title>
							<path d="M19.0125 3.52499C19.0125 1.94999 17.775 0.674988 16.2375 0.674988H7.7625C6.225 0.674988 4.9875 1.94999 4.9875 3.52499V20.475C4.9875 22.05 6.225 23.325 7.7625 23.325H16.2C17.7375 23.325 18.975 22.05 18.975 20.475V3.52499H19.0125ZM6.675 7.49999H17.2875V17.9625H6.675V7.49999ZM7.7625 2.36249H16.2C16.8 2.36249 17.2875 2.88749 17.2875 3.52499V5.81249H6.675V3.52499C6.675 2.88749 7.1625 2.36249 7.7625 2.36249ZM16.2375 21.675H7.7625C7.1625 21.675 6.675 21.15 6.675 20.5125V19.65H17.2875V20.5125C17.325 21.1125 16.8375 21.675 16.2375 21.675Z"/>
							<path d="M11.3625 4.98748H12.7125C13.1625 4.98748 13.575 4.61248 13.575 4.12498C13.575 3.63748 13.2 3.26248 12.7125 3.26248H11.3625C10.9125 3.26248 10.5 3.63748 10.5 4.12498C10.5 4.61248 10.9125 4.98748 11.3625 4.98748Z"/>
						</svg>

						<svg v-else-if="icon === InputIcon.EDIT && !props.disabled" xmlns="http://www.w3.org/2000/svg" class="fill-current group-hover:text-primary-hover" width="20" height="20" viewBox="0 0 20 20">
							<title>Edit text</title>
							<path fill-rule="evenodd" clip-rule="evenodd" d="M1.56622 3.23223C2.03506 2.76339 2.67094 2.5 3.33398 2.5H9.16732C9.62755 2.5 10.0006 2.8731 10.0006 3.33333C10.0006 3.79357 9.62755 4.16667 9.16732 4.16667H3.33398C3.11297 4.16667 2.90101 4.25446 2.74473 4.41074C2.58845 4.56702 2.50065 4.77899 2.50065 5V16.6667C2.50065 16.8877 2.58845 17.0996 2.74473 17.2559C2.90101 17.4122 3.11297 17.5 3.33398 17.5H15.0006C15.2217 17.5 15.4336 17.4122 15.5899 17.2559C15.7462 17.0996 15.834 16.8877 15.834 16.6667V10.8333C15.834 10.3731 16.2071 10 16.6673 10C17.1276 10 17.5006 10.3731 17.5006 10.8333V16.6667C17.5006 17.3297 17.2373 17.9656 16.7684 18.4344C16.2996 18.9033 15.6637 19.1667 15.0006 19.1667H3.33398C2.67094 19.1667 2.03506 18.9033 1.56622 18.4344C1.09738 17.9656 0.833984 17.3297 0.833984 16.6667V5C0.833984 4.33696 1.09738 3.70107 1.56622 3.23223Z"/>
							<path fill-rule="evenodd" clip-rule="evenodd" d="M16.6673 2.39909C16.4195 2.39909 16.1818 2.49754 16.0066 2.67278L8.25314 10.4262L7.81264 12.1882L9.57463 11.7477L17.3281 3.99427C17.5033 3.81903 17.6018 3.58135 17.6018 3.33352C17.6018 3.0857 17.5033 2.84802 17.3281 2.67278C17.1528 2.49754 16.9152 2.39909 16.6673 2.39909ZM14.8281 1.49427C15.3159 1.00647 15.9775 0.732422 16.6673 0.732422C17.3572 0.732422 18.0188 1.00647 18.5066 1.49427C18.9944 1.98207 19.2684 2.64367 19.2684 3.33352C19.2684 4.02338 18.9944 4.68498 18.5066 5.17278L10.5899 13.0894C10.4831 13.1962 10.3493 13.272 10.2028 13.3086L6.86945 14.142C6.58547 14.213 6.28506 14.1298 6.07808 13.9228C5.8711 13.7158 5.78789 13.4154 5.85888 13.1314L6.69222 9.79808C6.72885 9.65155 6.80461 9.51773 6.91141 9.41093L14.8281 1.49427Z"/>
						</svg>

						<!-- Show Edit/View text only when props.useEditModal. Shows 'View' when disabled. -->
						<span v-if="props.useEditModal" class="clickable-text" @click="editModalButtonClicked">{{ props.disabled ? 'View' : 'Edit' }}</span>
					</slot>
				</div>
			</div>
		</div>

		<ValidationError :errors="props.errors" :field="props.errorField" class="mt-2 ml-1" />

		<Teleport to="html">
			<TextEntryModal
				v-if="showEditModal && editModalConfig"
				:show="true"
				:title="editModalConfig.title"
				:message="editModalConfig.message"
				:twoButtonModal="true"
				:button-size="editModalConfig.buttonSize"
				:button-width="editModalConfig.buttonWidth"
				:button-text="editModalConfig.buttonText"
				:default-text="editModalConfig.text"
				@onButtonClicked="editModalConfig.onButtonClicked"
				@dismiss="showEditModal = false"
				:max-length="editModalConfig.maxLength"
				:placeholder="editModalConfig.placeholder"
				:disabled="editModalConfig.disabled"
				:loading="editModalConfig.loading?.value ?? false"
				:input-filter="editModalConfig.inputFilter"
			/>
		</Teleport>
	</div>

</template>

<script setup lang="js">
import ValidationError from '@/components/Forms/InputError.vue'
import { ApiGatewayResponse } from '@/api/ApiGateway.js'
import { defineModel, ref, watch } from 'vue'
import { InputIcon } from '@/models/enums/InputIcon.js'
import Popper from 'vue3-popper'
import TextEntryModal from '@/components/Alerts/TextEntryModal.vue'

const model = defineModel()

const props = defineProps({
	label: { type: String, default: '' },
	labelTextSize: { type: String, default: '' },
	type: { type: String, default: 'text' },
	autocomplete: { type: String, default: 'off' },
	placeholder: { type: String, default: '' },
	icon: { type: String, default: 'none' },
	required: { type: Boolean, default: false },//true border red when value blank
	disabled: { type: Boolean, default: false },//true sets text-disabled
	inputBgColor: { type: String, default: 'bg-neutral' },
	errors: { type: ApiGatewayResponse, default: null },
	errorField: { type: String, default: null },
	textAreaRows: { type: String, default: '6' },
	customClass: { type: String, default: '' },
	hoverEdit: { type: Boolean, default: false }, //Set true if editing only occurs when hovering over input
	inputWidth: { type: String, default: 'w-full' },
	inputHeight: { type: String, default: null },
	tooltip: { type: String, default: null },
	tooltipLocation: {
		type: String,
		default: 'auto',
		validator(value)
		{
			// Values copied from Popper's 'placement' property
			return ['auto', 'auto-start', 'auto-end', 'top', 'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end', 'right', 'right-start', 'right-end', 'left', 'left-start', 'left-end'].includes(value)
		}
	},
	maxLength: { type: Number, default: 2000 },
	inputRadius: { type: String, default: 'rounded' },
	fontWeight: { type: String, default: 'font-medium' },
	useEditModal: { type: Boolean, default: false }, //True means field is not directly editable but will show a multiline popup to edit
	editModalConfig: { type: Object, default: null },
	inputFilter: { type: Function, default: null },
	beforeInput: { type: Function, default: null },
})

const showEditModal = ref(false)

//Prevent data exceeding max length if number (type=number doesn't respect maxlength attribute)
// Firefox allows entering letters (which mozilla claims it shouldn't), so this janky code is preventing it for now
// Needs updating to support floats/negatives if needed.
watch(model, (p_new_val, p_old_val) =>
{
	if (props.type !== 'number') return;
	let newValue = p_new_val?.toString() ?? ''
	newValue = newValue.replace(/[^0-9]/g, '')

	newValue = newValue.slice(0, props.maxLength)

	if (newValue.length > props.maxLength)
	{
		model.value = newValue.slice(0, props.maxLength)
	}
	else if (p_new_val !== "" && isNaN(parseFloat(newValue)))
	{
		model.value = p_old_val
	}
	else
	{
		model.value = newValue
	}
})

function editModalButtonClicked() {
	showEditModal.value = true
}

function calculatedClasses()
{
	let border = 'border-neutral';
	let error_border = 'border-error focus:border-error hover:border-error hover:ring-1 hover:ring-error focus:ring-error'

	if (props.hoverEdit)
	{
		border = 'border-none hover:border-neutral'
	}

	const classes = [
		'userpilot-mask w-full p-4 outline-none placeholder-default',
		//When disabled, don't show focus border
		props.disabled ? 'text-disabled' : 'text-body group-hover:border-primary-bold-hover group-hover:ring-1 group-hover:ring-primary-bold-hover focus:ring-1 focus:ring-neutral-subtle focus:bg-neutral focus:border-neutral-subtle' ,
		props.icon !== InputIcon.NONE ? 'pr-12' : '',
		props.useEditModal ? (props.disabled ? 'pr-16 text-ellipsis' : 'pr-14 text-ellipsis') : '',
		props.inputBgColor,
		hasError() || (props.required && model.value === "") ? error_border : border,
		props.type === 'textarea' || !props.inputHeight ? '' : props.inputHeight,
		props.inputWidth,
		props.inputRadius,
		props.fontWeight,
		props.customClass
	]
	return classes.join(' ')
}

function hasError()
{
	return props.errors?.validationErrors()?.find((error) => error.source === props.errorField) != null
}

</script>

<style scoped>
input::placeholder, textarea::placeholder {
	font-weight: 500;
	line-height: 24px;
}
</style>